import type { EmotionCache } from '@emotion/cache';
import createCache from '@emotion/cache';

/**
 * Pins the order of runtime-injected style tags, so that CSS specificity ties are decided by a
 * guaranteed head order instead of by whichever style happens to be created first.
 *
 * A marker is appended to the end of <head> on first use — by then the entry chunk's build CSS
 * (index.css, statically imported *.module.scss, Tailwind) is already mounted, in dev and
 * production alike. MUI's Emotion cache is pinned to it, and tss-react gets its own unpinned cache
 * so its tags append after. Resulting order, later wins ties:
 *
 *   entry build CSS  →  MUI Emotion  →  tss-react
 *
 * Pinning MUI is what makes that order independent of *when* each style is created. Emotion keeps a
 * cache's tags contiguous after its insertion point, so MUI's cluster stays above the unpinned tss
 * tags at the end of head even if a tss style is created first. Unpinned, the two would interleave
 * by render order and app overrides would sometimes lose to the component styles they override.
 *
 * The tss cache must be provided explicitly through TssCacheProvider: tss-react's cache lookup
 * falls back to the nearest Emotion CacheProvider, which is the pinned MUI cache — without the
 * dedicated provider, tss styles would insert at the MUI marker instead of last.
 *
 * Lazy-route CSS chunks are the one exception: Vite's preload helper appends their <link> to the
 * end of <head> when the route loads, after both zones, so they win same-specificity ties against
 * app styles. Measured in a production build: entry CSS, marker, MUI's zone, tss, then the lazy
 * <link> last. Such a tie is reachable rather than hypothetical — those sheets carry flat
 * single-class rules, the same specificity a tss class has — so prefer specificity over order for
 * any same-property collision on a lazy route. Do not reach for cssCodeSplit: false either: it
 * folds lazy chunks' CSS into the entry <link>, which mounts before the runtime marker — every
 * pinned zone would then beat CSS that today beats them, changing those ties app-wide.
 *
 * Both instances are lazy module-scope singletons: lazy so the marker lands after all build CSS,
 * singletons so Storybook decorators re-running per story reuse them instead of fragmenting order.
 */

interface StyleOrderInstances {
  muiEmotionCache: EmotionCache;
  tssEmotionCache: EmotionCache;
}

let instances: StyleOrderInstances | null = null;

const createHeadMarker = (name: string): HTMLElement => {
  const marker = document.createElement('meta');
  marker.setAttribute('name', name);
  document.head.appendChild(marker);
  return marker;
};

const getInstances = (): StyleOrderInstances => {
  if (instances === null) {
    const muiEmotionInsertionPoint = createHeadMarker('mui-emotion-insertion-point');
    instances = {
      // key 'css' matches MUI's default cache, keeping generated class names unchanged
      muiEmotionCache: createCache({ key: 'css', insertionPoint: muiEmotionInsertionPoint }),
      // no insertionPoint: appends at the end of <head>, after the pinned zone
      tssEmotionCache: createCache({ key: 'tss' }),
    };
  }
  return instances;
};

export const getMuiEmotionCache = (): EmotionCache => getInstances().muiEmotionCache;

export const getTssEmotionCache = (): EmotionCache => getInstances().tssEmotionCache;
